import React, { useRef, useCallback, useState } from "react";
import { Box, Typography } from "@mui/material";
import DayCell from "./DayCell";

export interface MonthGridProps {
  year: number;
  month: number; // 0-11
  calendarData: Record<string, string>;
  activeParent: "parent1" | "parent2";
  holidays: Record<string, string>; // date -> holiday name
  onDateClick: (dateStr: string) => void;
  rangeStart: string; // ISO date — first day of range
  rangeEnd: string; // ISO date — last day of range
  parentName1: string;
  parentName2: string;
}

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_FULL_LABELS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const MonthGrid: React.FC<MonthGridProps> = ({
  year,
  month,
  calendarData,
  holidays,
  onDateClick,
  rangeStart,
  rangeEnd,
  parentName1,
  parentName2,
}) => {
  const rangeStartDate = new Date(rangeStart + "T00:00:00");
  const rangeEndDate = new Date(rangeEnd + "T00:00:00");

  // First day of this month and total days in the month
  const firstOfMonth = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startDayOfWeek = firstOfMonth.getDay(); // 0=Sun

  // Build ordered list of date strings for this month
  const dateCells: (string | null)[] = [];
  for (let i = 0; i < startDayOfWeek; i++) {
    dateCells.push(null); // leading blanks
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    dateCells.push(formatDate(date));
  }

  // Roving tabindex: track which cell is focusable within this grid
  const [focusedIndex, setFocusedIndex] = useState<number>(() => {
    // Default to first in-range day
    for (let i = 0; i < dateCells.length; i++) {
      const dateStr = dateCells[i];
      if (dateStr) {
        const date = new Date(dateStr + "T00:00:00");
        if (date >= rangeStartDate && date <= rangeEndDate) {
          return i;
        }
      }
    }
    return 0;
  });

  const cellRefs = useRef<(HTMLElement | null)[]>([]);

  const setCellRef = useCallback(
    (index: number) => (el: HTMLElement | null) => {
      cellRefs.current[index] = el;
    },
    []
  );

  /**
   * WCAG 2.1.1 Keyboard: Arrow key navigation within the grid.
   * - Left/Right: move by 1 day
   * - Up/Down: move by 1 week (7 cells)
   * - Home: first day of month
   * - End: last day of month
   * - Enter/Space: activate (click)
   */
  const handleKeyDown = useCallback(
    (index: number, dateStr: string) => (e: React.KeyboardEvent) => {
      let nextIndex: number | null = null;

      switch (e.key) {
        case "ArrowRight":
          nextIndex = findNextInRange(index, 1, dateCells, rangeStartDate, rangeEndDate);
          break;
        case "ArrowLeft":
          nextIndex = findNextInRange(index, -1, dateCells, rangeStartDate, rangeEndDate);
          break;
        case "ArrowDown":
          nextIndex = findNextInRange(index, 7, dateCells, rangeStartDate, rangeEndDate);
          break;
        case "ArrowUp":
          nextIndex = findNextInRange(index, -7, dateCells, rangeStartDate, rangeEndDate);
          break;
        case "Home":
          e.preventDefault();
          nextIndex = findFirstInRange(dateCells, rangeStartDate, rangeEndDate);
          break;
        case "End":
          e.preventDefault();
          nextIndex = findLastInRange(dateCells, rangeStartDate, rangeEndDate);
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          onDateClick(dateStr);
          return;
        default:
          return;
      }

      if (nextIndex !== null && nextIndex !== index) {
        e.preventDefault();
        setFocusedIndex(nextIndex);
        cellRefs.current[nextIndex]?.focus();
      }
    },
    [dateCells, rangeStartDate, rangeEndDate, onDateClick]
  );

  // Build rows of 7 cells each for proper table/grid semantics
  const rows: (string | null)[][] = [];
  for (let i = 0; i < dateCells.length; i += 7) {
    rows.push(dateCells.slice(i, i + 7));
  }
  // Pad last row to 7 cells
  const lastRow = rows[rows.length - 1];
  while (lastRow && lastRow.length < 7) {
    lastRow.push(null);
  }

  const monthLabel = `${MONTH_NAMES[month]} ${year}`;

  return (
    <Box sx={{ width: 280 }}>
      {/* Month header */}
      <Typography
        variant="subtitle1"
        align="center"
        sx={{ fontWeight: 600, mb: 0.5 }}
        id={`month-header-${year}-${month}`}
      >
        {monthLabel}
      </Typography>

      {/* Grid with proper ARIA table roles */}
      <Box
        role="grid"
        aria-labelledby={`month-header-${year}-${month}`}
        aria-label={`${monthLabel} calendar grid`}
      >
        {/* Column header row */}
        <Box role="row" sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5 }}>
          {DAY_LABELS.map((label, i) => (
            <Typography
              key={label}
              role="columnheader"
              variant="caption"
              align="center"
              aria-label={DAY_FULL_LABELS[i]}
              sx={{ fontWeight: 600, color: "text.secondary" }}
            >
              {label}
            </Typography>
          ))}
        </Box>

        {/* Date rows */}
        {rows.map((row, rowIndex) => (
          <Box
            key={rowIndex}
            role="row"
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: 0.5,
              justifyItems: "center",
            }}
          >
            {row.map((dateStr, colIndex) => {
              const cellIndex = rowIndex * 7 + colIndex;

              if (!dateStr) {
                return (
                  <Box
                    key={`blank-${cellIndex}`}
                    role="gridcell"
                    aria-hidden="true"
                    sx={{ width: 36, height: 36 }}
                  />
                );
              }

              const date = new Date(dateStr + "T00:00:00");
              const dayNumber = date.getDate();
              const isInRange = date >= rangeStartDate && date <= rangeEndDate;
              const assignment = calendarData[dateStr] as "parent1" | "parent2" | undefined;

              return (
                <DayCell
                  key={dateStr}
                  dateStr={dateStr}
                  dayNumber={dayNumber}
                  assignment={assignment ?? null}
                  holidayName={holidays[dateStr] ?? null}
                  isInRange={isInRange}
                  parentName1={parentName1}
                  parentName2={parentName2}
                  isFocused={cellIndex === focusedIndex}
                  onClick={() => onDateClick(dateStr)}
                  onKeyDown={handleKeyDown(cellIndex, dateStr)}
                  cellRef={setCellRef(cellIndex)}
                />
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// --- Arrow navigation helpers ---

function isInRange(dateStr: string, rangeStart: Date, rangeEnd: Date): boolean {
  const date = new Date(dateStr + "T00:00:00");
  return date >= rangeStart && date <= rangeEnd;
}

function findNextInRange(
  currentIndex: number,
  step: number,
  dateCells: (string | null)[],
  rangeStart: Date,
  rangeEnd: Date
): number | null {
  let nextIndex = currentIndex + step;
  // For single-step moves, just try the next cell
  if (Math.abs(step) === 1) {
    while (nextIndex >= 0 && nextIndex < dateCells.length) {
      const cell = dateCells[nextIndex];
      if (cell && isInRange(cell, rangeStart, rangeEnd)) {
        return nextIndex;
      }
      nextIndex += step;
    }
  } else {
    // For week jumps, try the target cell or closest available
    if (nextIndex >= 0 && nextIndex < dateCells.length) {
      const cell = dateCells[nextIndex];
      if (cell && isInRange(cell, rangeStart, rangeEnd)) {
        return nextIndex;
      }
    }
  }
  return null;
}

function findFirstInRange(
  dateCells: (string | null)[],
  rangeStart: Date,
  rangeEnd: Date
): number | null {
  for (let i = 0; i < dateCells.length; i++) {
    const cell = dateCells[i];
    if (cell && isInRange(cell, rangeStart, rangeEnd)) {
      return i;
    }
  }
  return null;
}

function findLastInRange(
  dateCells: (string | null)[],
  rangeStart: Date,
  rangeEnd: Date
): number | null {
  for (let i = dateCells.length - 1; i >= 0; i--) {
    const cell = dateCells[i];
    if (cell && isInRange(cell, rangeStart, rangeEnd)) {
      return i;
    }
  }
  return null;
}

export default MonthGrid;
